import type { AfterResponseHook } from "ky";
import ky from "ky";

import { hideLoader, showLoader } from "@/redux/actions/loader";
import store from "@/redux/store";

import type { ApiContext } from "../../types";
import { authRequestsTyped } from "../../(requests)/authTyped";
import { workflowTokenStore } from "../../helpers/workflowTokenStore";

import { handleSessionExpired } from "./sessionExpired";

const dispatch = store.dispatch;

// Workflow (guest) session — retries a 403 once by refreshing the in-memory workflowTokenStore.
export const refreshWorkflowTokenHook: AfterResponseHook = async ({
  options,
  request,
  response,
  retryCount,
}) => {
  if (response.status !== 403 || retryCount > 0) return undefined;

  const context = options.context as ApiContext | undefined;

  // Same loader pattern as refreshAuthTokenHook
  dispatch(showLoader());

  try {
    const newToken = await refreshWorkflowTokenOnce();

    // Respect per-error-type allowRetry: false
    const errorData = await response
      .clone()
      .json()
      .catch((): null => null);
    const errorType = (errorData as { error?: { type?: string } } | null)?.error?.type ?? "";
    const isRetryAllowed = context?.exceptionConfig?.[errorType]?.allowRetry !== false;

    if (!isRetryAllowed) {
      dispatch(hideLoader());

      return undefined;
    }

    const newHeaders = new Headers(request.headers);

    newHeaders.set("Authorization", newToken);

    // loaderEndHook fires for the retry response — no explicit hideLoader needed on success
    return ky.retry({
      code: "WORKFLOW_TOKEN_REFRESHED",
      request: new Request(request, { headers: newHeaders }),
    });
  } catch (err) {
    // No unconditional logOut() — mirrors old refreshGlobalTokens behavior.
    // handleSessionExpired redirects only on confirmed 401 (exhausted retries on
    // new_id_token). workflowTokenStore.clear() is already called inside
    // refreshWorkflowTokenOnce.
    dispatch(hideLoader());
    handleSessionExpired(err);

    return undefined;
  }
};

let workflowRefreshPromise: Promise<string> | null = null;

function refreshWorkflowTokenOnce(): Promise<string> {
  if (!workflowRefreshPromise) {
    const current = workflowTokenStore.get();

    // authRequestsTyped.newIdToken uses httpPublic (public.ts) internally —
    // no circular dep through the authenticated ky instances.
    workflowRefreshPromise = authRequestsTyped
      .newIdToken({ body: { id_token: current?.idToken, refresh_token: current?.refreshToken } })
      .then((res) => {
        const newIdToken = res.AuthenticationResult.IdToken;

        workflowTokenStore.set({
          idToken: newIdToken,
          refreshToken: current?.refreshToken ?? "",
        });

        return newIdToken;
      })
      .catch((err: unknown) => {
        workflowTokenStore.clear();

        return Promise.reject(err);
      })
      .finally(() => {
        workflowRefreshPromise = null;
      });
  }

  return workflowRefreshPromise;
}
